import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Shield, Calendar, Users, Clock, CheckCircle, AlertCircle, Phone, Menu, X, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import TeamMemberSelection from './components/TeamMemberSelection';
import AppointmentCalendar from './components/AppointmentCalendar';
import MeetingPurpose from './components/MeetingPurpose';
import ConfirmationStep from './components/ConfirmationStep';
import { appointmentService } from '../../services/appointmentService';
import { isValidPhoneNumber } from '../../utils/phoneFormatter';

export default function AppointmentSchedulingSystem() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Form data state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [purpose, setPurpose] = useState('');
  const [purposeDetails, setPurposeDetails] = useState('');
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => prev + 1);
      setError('');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!selectedAgent) {
          setError('Please select a team member');
          return false;
        }
        return true;
      case 2:
        if (!selectedDate || !selectedTime) {
          setError('Please select date and time');
          return false;
        }
        return true;
      case 3:
        if (!purpose) {
          setError('Please select meeting purpose');
          return false;
        }
        if (!clientInfo?.name || !clientInfo?.email || !clientInfo?.phone) {
          setError('Please fill in all contact information');
          return false;
        }
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex?.test(clientInfo?.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        // Phone validation
        if (!isValidPhoneNumber(clientInfo?.phone)) {
          setError('Please enter a valid 10-digit phone number');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    try {
      setLoading(true);
      setError('');

      console.log('🚀 Starting appointment submission...');

      // Combine date and time
      const appointmentDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime?.split(':');
      appointmentDateTime?.setHours(parseInt(hours), parseInt(minutes));

      console.log('📅 Appointment date/time:', appointmentDateTime?.toISOString());

      // Check for conflicts
      console.log('🔍 Checking for appointment conflicts...');
      const hasConflict = await appointmentService?.checkAppointmentConflict(
        selectedAgent?.id,
        appointmentDateTime?.toISOString()
      );

      console.log('🔍 Conflict check result:', hasConflict);

      if (hasConflict) {
        console.warn('⚠️ Appointment conflict detected');
        
        // Get details of conflicting appointment for better error message
        const conflictDetails = await appointmentService?.getConflictingAppointmentDetails(
          selectedAgent?.id,
          appointmentDateTime?.toISOString()
        );

        if (conflictDetails) {
          const conflictTime = new Date(conflictDetails?.appointment_date)?.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });

          setError(`This time slot is already booked. ${selectedAgent?.fullName} has an appointment at ${conflictTime}. Please select a different time at least 30 minutes away from existing appointments.`);
        } else {
          setError('This time slot is already booked. Please select a different time at least 30 minutes away from existing appointments.');
        }
        
        setCurrentStep(2);
        return;
      }

      console.log('✅ No conflicts found, proceeding with appointment creation...');

      // Create appointment
      console.log('📝 Creating appointment record...');
      const appointment = await appointmentService?.createAppointment({
        clientName: clientInfo?.name,
        clientEmail: clientInfo?.email,
        clientPhone: clientInfo?.phone,
        bailAgentId: selectedAgent?.id,
        appointmentDate: appointmentDateTime?.toISOString(),
        purpose,
        purposeDetails
      });

      console.log('✅ Appointment created with ID:', appointment?.id);

      // Extract approval token - handle both snake_case and camelCase
      const approvalToken = appointment?.approval_token || appointment?.approvalToken;
      
      // Extract agent details from nested bail_agents object first, fallback to selectedAgent
      const agentFromDb = appointment?.bail_agents;
      const agentEmail = agentFromDb?.email || selectedAgent?.email;
      const agentName = agentFromDb?.full_name || selectedAgent?.fullName;
      
      // Validate all required fields before calling Edge Function
      if (!approvalToken) {
        console.error('❌ Missing approval token in appointment response:', { 
          appointmentKeys: Object.keys(appointment || {}),
          approvalToken 
        });
        throw new Error('Failed to generate approval token');
      }

      if (!agentEmail) {
        console.error('❌ Missing agent email:', { 
          agentFromDb,
          selectedAgent,
          agentEmail 
        });
        throw new Error('Agent email not available for notification');
      }

      if (!agentName) {
        console.error('❌ Missing agent name:', { 
          agentFromDb,
          selectedAgent,
          agentName 
        });
        throw new Error('Agent name not available for notification');
      }

      console.log('🔐 Approval token retrieved:', approvalToken?.substring(0, 8) + '...');
      console.log('📧 Agent email:', agentEmail);
      console.log('👤 Agent name:', agentName);
      console.log('📝 Preparing to send approval email with params:', {
        appointmentId: appointment?.id,
        agentEmail,
        agentName,
        clientName: clientInfo?.name,
        appointmentDate: appointmentDateTime?.toISOString(),
        purpose,
        purposeDetails: purposeDetails || '',
        approvalTokenLength: approvalToken?.length
      });

      // Send approval email to agent with all validated parameters
      console.log('📧 Sending approval email to agent...');
      try {
        await appointmentService?.sendApprovalEmail(
          appointment?.id,
          agentEmail,
          agentName,
          clientInfo?.name,
          appointmentDateTime?.toISOString(),
          purpose,
          purposeDetails || '', // Ensure empty string instead of null/undefined
          approvalToken,
          appointment?.confirmation_number  // 🆕 ADD: Pass confirmation number to agent email
        );
        console.log('✅ Approval email sent successfully');
      } catch (emailError) {
        console.error('❌ Failed to send approval email:', emailError);
        console.error('📧 Email parameters that failed:', {
          appointmentId: appointment?.id,
          agentEmail,
          agentName,
          clientName: clientInfo?.name,
          appointmentDate: appointmentDateTime?.toISOString(),
          purpose,
          purposeDetails: purposeDetails || '',
          approvalToken: approvalToken?.substring(0, 8) + '...'
        });
        // Rethrow to be caught by outer catch
        throw new Error(`Failed to send approval notification: ${emailError?.message || 'Unknown error'}`);
      }

      console.log('🎉 Appointment process completed successfully!');

      setSuccess(true);
      setCurrentStep(4);
    } catch (err) {
      console.error('❌ Appointment submission error:', err);
      setError(err?.message || 'Failed to schedule appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAgentEmail = (fullName) => {
    // DEPRECATED: Email now comes from database via selectedAgent.email
    // Keeping this function as fallback only for backward compatibility
    const emailMap = {
      'Damian Sailors': 'damian@sailorsbail.com',
      'Michael Sailors': 'michael@sailorsbail.com',
      'Sarah Bane': 'sarah@sailorsbail.com'
    };
    return emailMap?.[fullName] || 'info@sailorsbail.com';
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedAgent(null);
    setSelectedDate(null);
    setSelectedTime('');
    setPurpose('');
    setPurposeDetails('');
    setClientInfo({ name: '', email: '', phone: '' });
    setSuccess(false);
    setError('');
  };

  const progressPercentage = (currentStep / 4) * 100;

  return (
    <>
      <Helmet>
        <title>Schedule Appointment - Sailors Bail Bonds</title>
        <meta name="description" content="Schedule a consultation with our professional bail agents. Available 24/7 for emergency bail assistance." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Navigation Header */}
        <header 
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
          }`}
        >
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764521073501.png" 
                  alt="Sailors Bail Bonds logo"
                  className="h-16 w-auto"
                />
              </Link>

              {/* Client Portal Access Button - Desktop */}
              <div className="hidden lg:block">
                <Link 
                  to="/client-portal-dashboard"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-3xl font-medium transition-all transform hover:scale-105 shadow-md flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Client Access
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-8">
                <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Home
                </Link>
                <Link to="/about-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  About
                </Link>
                <Link to="/services" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Services
                </Link>
                <Link to="/locations" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Locations
                </Link>
                <Link to="/blog" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Blog
                </Link>
                <Link to="/appointment-scheduling-system" className="text-blue-600 font-bold transition-colors">
                  Schedule
                </Link>
                <Link to="/contact-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Contact
                </Link>
                
                {/* Emergency Button */}
                <a 
                  href="tel:9162563468"
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg flex items-center"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  24/7 Emergency Bail
                </a>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </button>
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <div className="lg:hidden py-4 border-t border-gray-200">
                <div className="flex flex-col space-y-4">
                  <Link 
                    to="/client-portal-dashboard"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-3xl font-medium text-center flex items-center justify-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Client Access
                  </Link>
                  <Link 
                    to="/" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Home
                  </Link>
                  <Link 
                    to="/about-us" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link 
                    to="/services" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Services
                  </Link>
                  <Link 
                    to="/locations" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Locations
                  </Link>
                  <Link 
                    to="/blog" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Blog
                  </Link>
                  <Link 
                    to="/appointment-scheduling-system" 
                    className="text-blue-600 font-bold transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Schedule
                  </Link>
                  <Link 
                    to="/contact-us" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                  <a 
                    href="tel:9162563468"
                    className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl font-bold text-center"
                  >
                    <Phone className="w-4 h-4 inline mr-2" />
                    24/7 Emergency Bail
                  </a>
                </div>
              </div>
            )}
          </nav>
        </header>

        {/* Main Content */}
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full mb-4">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Consultation
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              Booking an appointment with our team is easy. Simply select a team member, choose a convenient time, and provide your details. If the team member you selected is unavailable or if another team member is better suited to assist you, we may suggest a different team member. You will be notified of any changes via email and will have the option to approve, decline, or reschedule your appointment. Our goal is to ensure you receive timely and expert assistance.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-medium">Select Agent</span>
                </div>
                <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <Clock className="w-5 h-5" />
                  <span className="text-sm font-medium">Choose Time</span>
                </div>
                <div className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">Meeting Details</span>
                </div>
                <div className={`flex items-center gap-2 ${currentStep >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Confirmation</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-blue-700 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="max-w-4xl mx-auto mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-8">
                {currentStep === 1 && (
                  <TeamMemberSelection
                    selectedAgent={selectedAgent}
                    onSelectAgent={setSelectedAgent}
                  />
                )}

                {currentStep === 2 && (
                  <AppointmentCalendar
                    selectedAgent={selectedAgent}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    onSelectDate={setSelectedDate}
                    onSelectTime={setSelectedTime}
                    agentId={selectedAgent?.id}
                  />
                )}

                {currentStep === 3 && (
                  <MeetingPurpose
                    purpose={purpose}
                    purposeDetails={purposeDetails}
                    clientInfo={clientInfo}
                    onPurposeChange={setPurpose}
                    onPurposeDetailsChange={setPurposeDetails}
                    onClientInfoChange={setClientInfo}
                  />
                )}

                {currentStep === 4 && success && (
                  <ConfirmationStep
                    agent={selectedAgent}
                    appointmentDate={selectedDate}
                    appointmentTime={selectedTime}
                    purpose={purpose}
                    clientInfo={clientInfo}
                    onReset={resetForm}
                  />
                )}
              </div>

              {/* Navigation Buttons */}
              {currentStep < 4 && (
                <div className="bg-gray-50 px-8 py-6 flex items-center justify-between border-t">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="px-6 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Back
                  </button>

                  {currentStep < 3 ? (
                    <button
                      onClick={handleNext}
                      disabled={loading}
                      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          <span>Scheduling...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          <span>Schedule Appointment</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="max-w-4xl mx-auto mt-8 text-center">
            <p className="text-gray-600">
              Need immediate assistance? Call us 24/7 at{' '}
              <a href="tel:+19162563468" className="text-blue-600 font-semibold hover:text-blue-700">
                (916) 256-3468
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}